import { cn } from '../../../lib/cn'
import { useChartTheme } from './useChartTheme'
import { divergingColor, rampColor } from './palette'

export interface ChartLegendItem {
  label: string
  color: string
  /** Форма ключа: прямоугольник (столбцы, области) или линия (линии), пунктир — сравнение. */
  shape?: 'rect' | 'line' | 'dashed'
}

/** Легенда в HTML: ключ цвета метки + подпись цветом текста. */
export function ChartLegend({ items, className }: { items: ChartLegendItem[]; className?: string }) {
  if (items.length === 0) return null
  return (
    <ul className={cn('flex flex-wrap items-center gap-x-4 gap-y-1 text-xs text-gray-600 dark:text-zinc-300', className)}>
      {items.map((item) => (
        <li key={item.label} className="inline-flex items-center gap-1.5">
          {item.shape === 'line' || item.shape === 'dashed' ? (
            <span
              aria-hidden="true"
              className="inline-block w-3.5"
              style={
                item.shape === 'dashed'
                  ? { borderTop: `2px dashed ${item.color}`, opacity: 0.6 }
                  : { height: 2, background: item.color }
              }
            />
          ) : (
            <span aria-hidden="true" className="inline-block h-2 w-3.5 rounded-sm" style={{ background: item.color }} />
          )}
          {item.label}
        </li>
      ))}
    </ul>
  )
}

export interface ScaleLegendProps {
  scale: 'sequential' | 'diverging'
  /** Подписи концов (и середины для расходящейся шкалы). */
  minLabel: string
  maxLabel: string
  midLabel?: string
  className?: string
}

/** Легенда непрерывной шкалы: градиент от «мало» к «много» (или от падения через «без изменений» к росту). */
export function ScaleLegend({ scale, minLabel, maxLabel, midLabel, className }: ScaleLegendProps) {
  const theme = useChartTheme()
  const stops =
    scale === 'diverging'
      ? [-1, -0.5, 0, 0.5, 1].map((value) => divergingColor(theme, value, 1))
      : [0, 0.25, 0.5, 0.75, 1].map((value) => rampColor(theme.sequential, value))
  return (
    <div className={cn('flex items-center gap-2 text-xs text-gray-500 dark:text-zinc-400', midLabel && 'pb-3', className)}>
      <span>{minLabel}</span>
      <span className="relative inline-block h-2 w-40 rounded-sm" style={{ background: `linear-gradient(to right, ${stops.join(', ')})` }} aria-hidden="true">
        {midLabel && <span className="absolute left-1/2 top-full mt-0.5 -translate-x-1/2 whitespace-nowrap text-2xs">{midLabel}</span>}
      </span>
      <span>{maxLabel}</span>
    </div>
  )
}

export default ChartLegend
